/**
 * Podcast band — transport for the featured episode player, plus optional RSS
 * hydration for the featured track and the latest-episodes list.
 *
 * Figma `Content / Podcast Player` 5326:6030. Every control the design draws
 * maps onto an HTMLMediaElement property, so the speed, skip, mute, progress
 * and time readouts are all real values read off the `<audio>` element rather
 * than static chrome. Theme settings → Podcast → RSS feed URL supplies audio
 * and list rows when merchant blocks leave those blank / when Load episodes
 * from RSS is on.
 */

import {getRssFeedItems, parseTimestamp} from './component-podcast-feed.js'; // eslint-disable-line import/extensions

const SPEED_STEPS = [1, 1.25, 1.5, 1.75, 2];
const COPIED_MESSAGE_DURATION = 2000;

/**
 * Split a feed title like "193: You Are Entitled to Nothing" into number + display title.
 * Kept local so Shopify's unversioned relative ESM import of the shared feed
 * module does not need new named exports (browser-cached modules omit them).
 *
 * @param {string} title
 * @returns {{ number: string, displayTitle: string }}
 */
const splitEpisodeTitle = (title) => {
  if (!title) {
    return {number: '', displayTitle: ''};
  }

  const match = title.trim().match(/^(\d+)\s*:\s*(.+)$/);

  if (!match) {
    return {number: '', displayTitle: title.trim()};
  }

  return {number: match[1], displayTitle: match[2].trim()};
};

/**
 * Format an itunes:duration value ("HH:MM:SS", "MM:SS", or seconds) as "N min".
 *
 * @param {string} duration
 * @returns {string}
 */
const formatDurationMinutes = (duration) => {
  if (!duration) {
    return '';
  }

  const trimmed = duration.trim();
  let seconds = Number.NaN;

  if (trimmed.includes(':')) {
    seconds = parseTimestamp(trimmed);
  } else {
    seconds = Number(trimmed);
  }

  if (!Number.isFinite(seconds) || seconds < 0) {
    return '';
  }

  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes} min`;
};

/**
 * Formats a number of seconds as `m:ss`, or `h:mm:ss` past the hour.
 * @param {number} seconds
 * @returns {string} Empty string when the duration is not yet known.
 */
const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) { return ''; }

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const paddedSeconds = String(total % 60).padStart(2, '0');

  if (hours > 0) { return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`; }

  return `${minutes}:${paddedSeconds}`;
};

/**
 * @param {string} left
 * @param {string} right
 */
const titlesMatch = (left, right) => {
  const normalize = (value) =>
    value
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  return normalize(left) === normalize(right);
};

if (!customElements.get('podcast-band')) {
  class PodcastBand extends HTMLElement {
    abortController = undefined;
    copiedTimeout = undefined;
    speedIndex = 0;
    listLoaded = false;
    currentFeedTitle = '';

    get audio() {
      return this.querySelector('[data-podcast-audio]');
    }

    get toggle() {
      return this.querySelector('[data-podcast-toggle]');
    }

    get speedButton() {
      return this.querySelector('[data-podcast-speed]');
    }

    get speedLabel() {
      return this.querySelector('[data-podcast-speed-label]');
    }

    get muteButton() {
      return this.querySelector('[data-podcast-mute]');
    }

    get progress() {
      return this.querySelector('[data-podcast-progress]');
    }

    get elapsed() {
      return this.querySelector('[data-podcast-elapsed]');
    }

    get duration() {
      return this.querySelector('[data-podcast-duration]');
    }

    get shareButton() {
      return this.querySelector('[data-podcast-share]');
    }

    get status() {
      return this.querySelector('[data-podcast-status]');
    }

    connectedCallback() {
      this.abortController = new AbortController();
      this.setupShare();
      this.setupTransport();
      this.setupEpisodeList();
      this.hydrateFromRss();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.copiedTimeout);
    }

    /**
     * Sharing is a scripted action, so the button ships hidden and is revealed
     * only once this element upgrades.
     */
    setupShare() {
      const button = this.shareButton;
      if (!button) { return; }

      button.hidden = false;
      button.addEventListener('click', this.onShareClick.bind(this), {
        signal: this.abortController.signal,
      });
    }

    setupEpisodeList() {
      const {signal} = this.abortController;
      this.listPanel = this.querySelector('[data-podcast-list-panel]');
      this.listStatus = this.querySelector('[data-podcast-list-status]');
      this.list = this.querySelector('[data-podcast-list]');
      this.listPanel?.addEventListener('toggle', (event) => this.onListToggle(event), {signal});
      this.list?.addEventListener('click', (event) => this.onListPick(event), {signal});
    }

    /**
     * @param {ToggleEvent} event
     */
    async onListToggle(event) {
      if (event.newState !== 'open' || this.listLoaded) { return; }

      const feedUrl = this.dataset.rssFeedUrl;
      if (!feedUrl || !this.list || !this.listStatus) { return; }

      try {
        const items = await getRssFeedItems(feedUrl);
        this.listLoaded = true;
        this.fillEpisodeList(items.filter((item) => item.title && item.audioUrl));
      } catch (error) {
        this.listStatus.hidden = false;
        this.listStatus.textContent = this.listStatus.dataset.error || this.listStatus.textContent;
        this.list.hidden = true;
      }
    }

    /**
     * @param {Awaited<ReturnType<typeof getRssFeedItems>>} items
     */
    fillEpisodeList(items) {
      if (!this.list || !this.listStatus) { return; }

      this.list.replaceChildren();
      const currentTitle = this.currentFeedTitle || this.dataset.rssEpisodeTitle || '';
      const markLatest = !currentTitle && this.hasAttribute('data-rss-latest');
      let marked = false;

      for (const item of items) {
        const li = document.createElement('li');
        li.className = 'podcast-player__list-item';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'podcast-player__list-button';
        button.dataset.podcastListSrc = item.audioUrl;
        button.dataset.podcastListTitle = item.title;
        if (item.imageUrl) { button.dataset.podcastListArtwork = item.imageUrl; }

        const {displayTitle} = splitEpisodeTitle(item.title);
        button.textContent = displayTitle || item.title;

        const isCurrent = currentTitle
          ? titlesMatch(item.title, currentTitle)
          : markLatest && !marked;

        if (isCurrent) {
          marked = true;
          button.classList.add('podcast-player__list-button--current');
          button.setAttribute('aria-current', 'true');
        }

        li.append(button);
        this.list.append(li);
      }

      if (this.list.children.length === 0) {
        this.listStatus.hidden = false;
        this.listStatus.textContent = this.listStatus.dataset.empty || this.listStatus.textContent;
        this.list.hidden = true;
        return;
      }

      this.listStatus.hidden = true;
      this.list.hidden = false;
    }

    /**
     * @param {MouseEvent} event
     */
    onListPick(event) {
      const button = event.target.closest('[data-podcast-list-src]');
      if (!(button instanceof HTMLButtonElement) || !this.audio) { return; }

      const src = button.dataset.podcastListSrc;
      const title = button.dataset.podcastListTitle || '';
      if (!src) { return; }

      this.audio.src = src;
      this.audio.currentTime = 0;

      const progress = this.progress;
      if (progress) { progress.value = 0; }

      const elapsed = this.elapsed;
      if (elapsed) { elapsed.textContent = ''; }

      const duration = this.duration;
      if (duration) { duration.textContent = ''; }

      this.currentFeedTitle = title;

      const titleNode = this.querySelector('[data-podcast-title]');
      if (titleNode && title) {
        const {displayTitle} = splitEpisodeTitle(title);
        titleNode.textContent = displayTitle || title;
        titleNode.hidden = false;
      }

      const artwork = button.dataset.podcastListArtwork;
      if (artwork) { this.setPlayerArtwork(artwork); }

      for (const node of this.list?.querySelectorAll('[data-podcast-list-src]') || []) {
        const isCurrent = node === button;
        node.classList.toggle('podcast-player__list-button--current', isCurrent);
        if (isCurrent) {
          node.setAttribute('aria-current', 'true');
        } else {
          node.removeAttribute('aria-current');
        }
      }

      this.audio.play().catch(() => undefined);
      this.listPanel?.hidePopover?.();
    }

    setupTransport() {
      const {signal} = this.abortController;

      this.toggle?.addEventListener('click', this.onToggleClick.bind(this), {signal});
      this.speedButton?.addEventListener('click', this.onSpeedClick.bind(this), {signal});
      this.muteButton?.addEventListener('click', this.onMuteClick.bind(this), {signal});

      for (const button of this.querySelectorAll('[data-podcast-seek]')) {
        button.addEventListener('click', this.onSeekClick.bind(this), {signal});
      }

      // Delegated so RSS-built rows work without rebinding.
      this.addEventListener('click', this.onDelegatedEpisodeClick.bind(this), {signal});

      const audio = this.audio;
      if (!audio) { return; }

      audio.addEventListener('loadedmetadata', this.onLoadedMetadata.bind(this), {signal});
      audio.addEventListener('timeupdate', this.onTimeUpdate.bind(this), {signal});
      audio.addEventListener('play', this.onPlaybackStateChange.bind(this), {signal});
      audio.addEventListener('pause', this.onPlaybackStateChange.bind(this), {signal});
      audio.addEventListener('ended', this.onPlaybackStateChange.bind(this), {signal});

      // Metadata may already have arrived before this element upgraded.
      if (audio.readyState > 0) { this.onLoadedMetadata(); }
    }

    /**
     * @param {MouseEvent} event
     */
    onDelegatedEpisodeClick(event) {
      const button = event.target.closest('[data-podcast-episode]');
      if (!(button instanceof HTMLButtonElement) || !this.contains(button)) { return; }

      this.onEpisodeClick({currentTarget: button});
    }

    async hydrateFromRss() {
      const feedUrl = this.dataset.rssFeedUrl;
      if (!feedUrl) { return; }

      let items = [];

      try {
        items = await getRssFeedItems(feedUrl);
      } catch (error) {
        return;
      }

      const playable = items.filter((item) => item.title && item.audioUrl);
      this.applyFeaturedFromRss(playable);
      this.hydrateEpisodeRows(playable);
    }

    /**
     * @param {Awaited<ReturnType<typeof getRssFeedItems>>} items
     */
    applyFeaturedFromRss(items) {
      const audio = this.audio;
      if (!audio || items.length === 0) { return; }

      const hasSrc = Boolean(audio.getAttribute('src') || audio.currentSrc);
      const matchTitle = this.dataset.rssEpisodeTitle || '';
      const useLatest = this.hasAttribute('data-rss-latest');

      let featured = null;

      if (matchTitle) {
        featured = items.find((item) => titlesMatch(item.title, matchTitle)) || null;
      } else if (useLatest || !hasSrc) {
        featured = items[0];
      }

      if (!featured) { return; }

      this.currentFeedTitle = featured.title;

      if (!hasSrc && featured.audioUrl) {
        audio.src = featured.audioUrl;
        audio.load();
      }

      const titleNode = this.querySelector('[data-podcast-title]');
      if (titleNode && (useLatest || !titleNode.textContent?.trim())) {
        const {displayTitle} = splitEpisodeTitle(featured.title);
        titleNode.textContent = displayTitle || featured.title;
        titleNode.hidden = false;
      }

      if (featured.imageUrl && !this.querySelector('.podcast-player__art-image')) {
        this.setPlayerArtwork(featured.imageUrl);
      }
    }

    /**
     * Resolve Episode block rows whose titles match feed items.
     *
     * @param {Awaited<ReturnType<typeof getRssFeedItems>>} items
     */
    hydrateEpisodeRows(items) {
      for (const button of this.querySelectorAll('[data-rss-match-title]')) {
        if (!(button instanceof HTMLButtonElement)) { continue; }

        const matchTitle = button.dataset.rssMatchTitle || '';
        const match = items.find((item) => titlesMatch(item.title, matchTitle));

        if (!match?.audioUrl) {
          button.disabled = true;
          continue;
        }

        button.dataset.podcastSrc = match.audioUrl;
        button.disabled = false;

        if (match.imageUrl && !button.dataset.podcastArtwork) {
          button.dataset.podcastArtwork = match.imageUrl;
        }

        const {number: titleNumber, displayTitle} = splitEpisodeTitle(match.title);
        const number = match.episodeNumber || titleNumber;
        const meta = formatDurationMinutes(match.duration);
        const row = button.closest('.episode-row');

        if (row instanceof HTMLElement) {
          const numberEl = row.querySelector('[data-episode-number]');
          if (numberEl instanceof HTMLElement && number && !numberEl.textContent?.trim()) {
            numberEl.textContent = number;
            numberEl.hidden = false;
          }

          const metaEl = row.querySelector('[data-episode-meta]');
          if (metaEl instanceof HTMLElement && meta && !metaEl.textContent?.trim()) {
            metaEl.textContent = meta;
            metaEl.hidden = false;
          }

          if (displayTitle && button.dataset.podcastTitle === matchTitle) {
            button.dataset.podcastTitle = displayTitle;
          }
        }
      }
    }

    /**
     * @param {string} url
     */
    setPlayerArtwork(url) {
      const art = this.querySelector('.podcast-player__art');
      if (!art) { return; }

      let image = art.querySelector('.podcast-player__art-image');
      if (!(image instanceof HTMLImageElement)) {
        image = document.createElement('img');
        image.className = 'podcast-player__art-image';
        image.alt = '';
        art.append(image);
      }

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
    }

    onToggleClick() {
      const audio = this.audio;
      if (!audio || !audio.src) { return; }

      if (audio.paused) {
        audio.play().catch(() => undefined);
        return;
      }

      audio.pause();
    }

    /**
     * Loads a listed episode into the player that already owns the transport.
     * The same file, while it is playing, pauses instead of restarting.
     * @param {{ currentTarget: EventTarget | null }} event
     */
    onEpisodeClick(event) {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement) || button.disabled) { return; }

      const audio = this.audio;
      const src = button.dataset.podcastSrc;
      if (!audio || !src) { return; }

      let next = '';
      try {
        next = new URL(src, window.location.href).href;
      } catch (error) {
        return;
      }

      if (audio.currentSrc === next && !audio.paused && !audio.ended) {
        audio.pause();
        return;
      }

      if (audio.currentSrc !== next) {
        audio.src = src;
        audio.currentTime = 0;

        const progress = this.progress;
        if (progress) { progress.value = 0; }

        const elapsed = this.elapsed;
        if (elapsed) { elapsed.textContent = ''; }

        const duration = this.duration;
        if (duration) { duration.textContent = ''; }

        this.showEpisode(button);
      }

      audio.play().catch(() => undefined);
    }

    /**
     * @param {HTMLButtonElement} button
     */
    showEpisode(button) {
      const title = this.querySelector('[data-podcast-title]');
      const nextTitle = button.dataset.podcastTitle;
      if (title && nextTitle) {
        title.textContent = nextTitle;
        title.hidden = false;
      }

      const artwork = button.dataset.podcastArtwork;
      if (!artwork) { return; }

      this.setPlayerArtwork(artwork);
    }

    onPlaybackStateChange() {
      const audio = this.audio;
      const toggle = this.toggle;
      if (!audio || !toggle) { return; }

      const isPlaying = !audio.paused && !audio.ended;
      toggle.classList.toggle('podcast-player__play--playing', isPlaying);

      const label = isPlaying ? toggle.dataset.podcastLabelPause : toggle.dataset.podcastLabelPlay;
      if (label) { toggle.setAttribute('aria-label', label); }
    }

    onSeekClick(event) {
      const audio = this.audio;
      if (!audio) { return; }

      const offset = Number(event.currentTarget.dataset.podcastSeek);
      if (!Number.isFinite(offset)) { return; }

      const upperBound = Number.isFinite(audio.duration) ? audio.duration : audio.currentTime + offset;
      audio.currentTime = Math.min(Math.max(audio.currentTime + offset, 0), upperBound);
    }

    onSpeedClick() {
      const audio = this.audio;
      const label = this.speedLabel;
      if (!audio) { return; }

      this.speedIndex = (this.speedIndex + 1) % SPEED_STEPS.length;
      const speed = SPEED_STEPS[this.speedIndex];
      audio.playbackRate = speed;

      if (label) { label.textContent = `${speed}${this.speedButton?.dataset.podcastSpeedSuffix ?? ''}`; }
    }

    onMuteClick() {
      const audio = this.audio;
      const button = this.muteButton;
      if (!audio || !button) { return; }

      audio.muted = !audio.muted;
      button.setAttribute('aria-pressed', String(audio.muted));

      const label = audio.muted ? button.dataset.podcastLabelUnmute : button.dataset.podcastLabelMute;
      if (label) { button.setAttribute('aria-label', label); }
    }

    onLoadedMetadata() {
      const audio = this.audio;
      if (!audio || !Number.isFinite(audio.duration)) { return; }

      const progress = this.progress;
      if (progress) { progress.max = audio.duration; }

      const duration = this.duration;
      if (duration) { duration.textContent = formatTime(audio.duration); }
    }

    onTimeUpdate() {
      const audio = this.audio;
      if (!audio) { return; }

      const progress = this.progress;
      if (progress) { progress.value = audio.currentTime; }

      const elapsed = this.elapsed;
      if (elapsed) { elapsed.textContent = formatTime(audio.currentTime); }
    }

    async onShareClick() {
      const url = window.location.href;

      if (navigator.share) {
        try {
          await navigator.share({title: document.title, url});
        } catch (error) {
          if (error.name !== 'AbortError') {
            // Share failed for a non-cancel reason; fall through quietly.
          }
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(url);
        this.announceCopied();
      } catch (error) {
        // Clipboard write failed; nothing further to offer.
      }
    }

    announceCopied() {
      const status = this.status;
      const message = this.shareButton?.dataset.podcastLabelCopied;
      if (!status || !message) { return; }

      status.textContent = message;
      clearTimeout(this.copiedTimeout);
      this.copiedTimeout = setTimeout(() => {
        status.textContent = '';
      }, COPIED_MESSAGE_DURATION);
    }
  }

  customElements.define('podcast-band', PodcastBand);
}
